"use client"

import { useEffect, useState } from "react"
import { ChevronDownIcon } from "@radix-ui/react-icons"
import { MailIcon, MessageSquareIcon, PhoneCallIcon, XIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/components"
import UbRichTextCard from "@/components/storyblok/UbRichTextCard"

interface StoryblokServicePhoneProps {
  blok: any
  sbLocale: string
  oci?: boolean
  variant?: "default" | "rebrush"
}

export default function StoryblokServicePhone(props: StoryblokServicePhoneProps) {
  const [popUpOpen, setPopUpOpen] = useState(false)

  const closePopup = () => {
    setPopUpOpen(false)
  }

  const isOCI = props.oci ?? false
  const variant = props.variant ?? "default"
  const menuText = isOCI ? props.blok?.menu_text_oci : props.blok?.menu_text
  const headline = isOCI ? props.blok?.headline_oci : props.blok?.headline
  const serviceText1 = isOCI ? props.blok?.service_text_1_oci : props.blok?.service_text_1
  const serviceText2 = isOCI ? props.blok?.service_text_2_oci : props.blok?.service_text_2
  const serviceText3 = isOCI ? props.blok?.service_text_3_oci : props.blok?.service_text_3

  const isRichtextEmpty = (rt: any): boolean => {
    if (!rt) return true
    if (typeof rt === "string") return rt.trim().length === 0
    const hasMeaningfulContent = (node: any): boolean => {
      if (!node) return false
      if (Array.isArray(node)) return node.some((n) => hasMeaningfulContent(n))
      if (typeof node === "object") {
        if (node.type === "text" && typeof node.text === "string" && node.text.trim().length > 0) return true
        // Treat non-text leaf nodes as content as well (e.g., images, embedded blocks)
        if (["image", "emoji", "blok"].includes(node.type)) return true
        if (node.content) return hasMeaningfulContent(node.content)
      }
      return false
    }
    const root = (rt as any).content ?? rt
    return !hasMeaningfulContent(root)
  }

  const showServiceText1 = !isRichtextEmpty(serviceText1)
  const showServiceText2 = !isRichtextEmpty(serviceText2)
  const showServiceText3 = !isRichtextEmpty(serviceText3)

  useEffect(() => {
    if (variant !== "rebrush" || !popUpOpen) {
      return
    }

    const closeOnScroll = () => setPopUpOpen(false)
    window.addEventListener("scroll", closeOnScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", closeOnScroll)
    }
  }, [popUpOpen, variant])

  return (
    <div className={cn("items-center justify-center", variant === "rebrush" ? "flex" : "hidden md:flex")}>
      <Popover open={popUpOpen} onOpenChange={setPopUpOpen}>
        <PopoverTrigger
          aria-label={menuText}
          className={cn(
            variant === "rebrush" &&
              "inline-flex items-center justify-center rounded-[2px] bg-transparent leading-none focus-visible:ring-2 focus-visible:ring-ub-hlblue focus-visible:outline-hidden"
          )}
        >
          <div
            className={cn(
              "flex flex-row items-center justify-center",
              variant === "rebrush"
                ? "group inline-flex gap-1.5 text-sm leading-4 font-normal text-white"
                : "text-xs font-bold tracking-tight xl:tracking-normal"
            )}
          >
            <PhoneCallIcon
              className={variant === "rebrush" ? "size-4 text-white" : "mr-2 h-[20px] text-black"}
              strokeWidth={variant === "rebrush" ? 1.2 : 2}
            />
            <p className={cn("text-nowrap leading-4", variant === "rebrush" && "group-hover:underline")}>{menuText}</p>
            <ChevronDownIcon className={variant === "rebrush" ? "size-4 text-white" : "size-5 text-black"} />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            variant === "rebrush"
              ? "w-[min(calc(100vw-2rem),867px)] rounded-lg border-0 p-6 shadow-xl"
              : "mr-52 w-[610px] rounded-none border-black p-6"
          )}
        >
          <div className="flex w-full justify-between">
            <h2 className="mb-3.5 text-lg font-bold">{headline}</h2>
            <button onClick={closePopup} className="size-5">
              <XIcon className="size-5" />
            </button>
          </div>
          <div className="flex flex-row gap-5 text-sm">
            {showServiceText1 ? (
              <div className="flex grow flex-col border-r-2 border-gray-300 ">
                <PhoneCallIcon className="size-5"></PhoneCallIcon>
                <UbRichTextCard
                  blok={{ richtext: serviceText1 as any, _uid: "", component: "ub_rich_text_card" }}
                  sbLocale={props.sbLocale}
                />
              </div>
            ) : (
              <div className="flex grow flex-col border-r-2 border-gray-300">
                <PhoneCallIcon className="size-5"></PhoneCallIcon>
                <a href="#" className="text-sm font-bold text-gray-500">
                  Telefon: 0203 28277-420
                </a>
                <p className="font-bold">Fax: 0203 28277-450</p>
                <p className="text-xs">Mo.-Do. 7:30 - 18:00 Uhr,</p>
                <p className="text-xs">Fr. 7:30 - 17:00 Uhr</p>
              </div>
            )}
            {showServiceText2 ? (
              <div className={`flex grow flex-col ${showServiceText3 ? "border-r-2 border-gray-300" : ""}`}>
                <MailIcon className="size-5"></MailIcon>
                <UbRichTextCard
                  blok={{ richtext: serviceText2 as any, _uid: "", component: "ub_rich_text_card" }}
                  sbLocale={props.sbLocale}
                />
              </div>
            ) : (
              <div className={`flex grow flex-col ${showServiceText3 ? "border-r-2 border-gray-300" : ""}`}>
                <MailIcon className="size-5"></MailIcon>
                <a href="#" className="text-sm font-bold text-gray-500">
                  E-Mail: service@udobaer.de
                </a>
                <a href="#" className="text-sm font-bold text-gray-500">
                  Kontaktformular
                </a>
                <a href="#" className="text-sm font-bold text-gray-500">
                  Newsletter
                </a>
              </div>
            )}

            {showServiceText3 && (
              <div className="flex grow flex-col">
                <MessageSquareIcon className="size-5"></MessageSquareIcon>
                <UbRichTextCard
                  blok={{ richtext: serviceText3 as any, _uid: "", component: "ub_rich_text_card" }}
                  sbLocale={props.sbLocale}
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
